import type {
  EmailAdministrationOverview,
  DeliveryPolicyReason,
} from "@reminder-tracker/backend-contracts";
import { useMutation, useQuery } from "convex/react";
import { Component, useRef, useState, type ReactNode } from "react";

import {
  getEmailAdministrationOverview,
  setEmailChannelPolicy,
  getDeliveryCapabilities,
} from "../../backend/functions";
import { ChannelPolicyReview } from "../operational-context/channel-policy-review";

type EmailMode = "disabled" | "test-only";
type Feedback = Readonly<{ kind: "error" | "success"; message: string }>;
type PendingCommand = Readonly<{
  commandKey: string;
  expectedRevision: number | null;
  mode: EmailMode;
  reason: DeliveryPolicyReason;
}>;

function opaqueKey(prefix: "cmd" | "corr"): string {
  if (typeof globalThis.crypto.randomUUID !== "function")
    throw new Error("A secure administration identifier is unavailable.");
  return `${prefix}_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

function modeLabel(mode: EmailMode): string {
  return mode === "disabled" ? "Disabled" : "Test only";
}

function isSecurityFailure(error: unknown): boolean {
  if (typeof error !== "object" || error === null || !("data" in error)) return false;
  const data = error.data;
  if (typeof data !== "object" || data === null || !("code" in data)) return false;
  return [
    "AUTHENTICATION_REQUIRED",
    "AUTHORIZATION_DENIED",
    "REAUTHENTICATION_REQUIRED",
    "MFA_REQUIRED",
  ].includes(String(data.code));
}

class EmailGovernanceBoundary extends Component<
  Readonly<{ children: ReactNode }>,
  Readonly<{ error?: unknown; hasError: boolean }>
> {
  public override state: Readonly<{ error?: unknown; hasError: boolean }> = { hasError: false };

  public static getDerivedStateFromError(error: unknown) {
    return { error, hasError: true };
  }

  public override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (isSecurityFailure(this.state.error)) throw this.state.error;
    return (
      <section className="push-governance push-governance-state" role="alert">
        <h2>Email information unavailable</h2>
        <p>
          No destination or reminder content has been shown. Refresh to try the aggregate read
          again.
        </p>
      </section>
    );
  }
}

export function ProtectedEmailGovernance(): React.JSX.Element {
  return (
    <EmailGovernanceBoundary>
      <ConnectedEmailGovernance />
    </EmailGovernanceBoundary>
  );
}

function ConnectedEmailGovernance(): React.JSX.Element {
  const [correlationId] = useState(() => opaqueKey("corr"));
  const overview = useQuery(getEmailAdministrationOverview, { correlationId });
  const capabilities = useQuery(getDeliveryCapabilities, { correlationId });
  const setPolicy = useMutation(setEmailChannelPolicy);
  const pendingCommand = useRef<PendingCommand | null>(null);
  const [pendingMode, setPendingMode] = useState<EmailMode | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  if (overview === undefined) {
    return (
      <section
        className="push-governance push-governance-state"
        aria-busy="true"
        aria-live="polite"
      >
        <h2>Email governance</h2>
        <p>Reading email aggregates…</p>
      </section>
    );
  }

  const changeMode = async (mode: EmailMode, reason: DeliveryPolicyReason): Promise<void> => {
    if (overview.policy.state === "configured" && overview.policy.mode === mode) {
      setFeedback({ kind: "success", message: `Email is already ${modeLabel(mode)}.` });
      return;
    }
    const expectedRevision =
      overview.policy.state === "configured" ? overview.policy.revision : null;
    const retained = pendingCommand.current;
    const command =
      retained !== null &&
      retained.mode === mode &&
      retained.reason === reason &&
      retained.expectedRevision === expectedRevision
        ? retained
        : { commandKey: opaqueKey("cmd"), expectedRevision, mode, reason };
    pendingCommand.current = command;
    setPendingMode(mode);
    setFeedback(null);
    try {
      const result = await setPolicy({ ...command, correlationId: opaqueKey("corr") });
      pendingCommand.current = null;
      setFeedback({
        kind: "success",
        message: `Email changed to ${modeLabel(result.mode)} at policy revision ${result.revision}.`,
      });
    } catch {
      setFeedback({
        kind: "error",
        message: "The email policy was not confirmed. Refresh the aggregate view before retrying.",
      });
    } finally {
      setPendingMode(null);
    }
  };

  return (
    <EmailGovernanceView
      canManagePolicy={capabilities?.canManagePolicy === true}
      overview={overview}
      pendingMode={pendingMode}
      feedback={feedback}
      onChangeMode={(mode, reason) => void changeMode(mode, reason)}
    />
  );
}

export function EmailGovernanceView({
  overview,
  pendingMode,
  feedback,
  onChangeMode,
  canManagePolicy = false,
}: Readonly<{
  overview: EmailAdministrationOverview;
  pendingMode: EmailMode | null;
  feedback: Feedback | null;
  onChangeMode: (mode: EmailMode, reason: DeliveryPolicyReason) => void;
  canManagePolicy?: boolean;
}>): React.JSX.Element {
  const currentMode = overview.policy.state === "configured" ? overview.policy.mode : null;
  return (
    <section className="push-governance" aria-labelledby="email-governance-title">
      <div className="section-head push-governance-head">
        <div>
          <h2 id="email-governance-title">Email governance</h2>
          <p>
            Email remains separate from membership. Shared Test can stop it or permit one bounded
            synthetic verification journey.
          </p>
        </div>
        <span className="push-environment">{overview.environment}</span>
      </div>
      <div className="push-policy">
        <div>
          <span className="push-policy-label">Current policy</span>
          <strong>{currentMode === null ? "Not configured" : modeLabel(currentMode)}</strong>
          <small>
            {overview.policy.state === "configured"
              ? `Revision ${overview.policy.revision}`
              : "No outbound email policy has been admitted in this environment."}
          </small>
        </div>
        <ChannelPolicyReview
          channel="Email"
          environment={overview.environment}
          currentMode={currentMode}
          revision={overview.policy.state === "configured" ? overview.policy.revision : null}
          pendingMode={pendingMode}
          onConfirm={onChangeMode}
          canManagePolicy={canManagePolicy}
        />
      </div>
      {feedback === null ? null : (
        <p
          className={`push-feedback ${feedback.kind}`}
          role={feedback.kind === "error" ? "alert" : "status"}
        >
          {feedback.message}
        </p>
      )}
      <div className="push-aggregate-groups">
        <Aggregate
          title="Contact lifecycle"
          values={[
            ["Waiting for recipient", overview.awaitingRecipientCount],
            ["Waiting for Owner", overview.awaitingOwnerApprovalCount],
            ["Active contacts", overview.activeContactCount],
            ["Ended contacts", overview.terminalContactCount],
          ]}
        />
        <Aggregate
          title="Provider attempts"
          values={[
            ["Accepted by Brevo", overview.acceptedAttemptCount],
            ["Failed attempts", overview.failedAttemptCount],
            ["Unknown outcomes", overview.unknownAttemptCount],
          ]}
        />
      </div>
      <div className="push-outcome-note">
        <strong>What acceptance means</strong>
        <p>
          Accepted means Brevo accepted the request. It does not prove inbox delivery, reading or
          consent. Consent and Owner approval remain separate durable decisions.
        </p>
      </div>
      <dl className="push-privacy-proof">
        <div>
          <dt>Destinations accessible</dt>
          <dd>No</dd>
        </div>
        <div>
          <dt>Reminder content accessible</dt>
          <dd>No</dd>
        </div>
      </dl>
    </section>
  );
}

function Aggregate({
  title,
  values,
}: Readonly<{
  title: string;
  values: ReadonlyArray<readonly [string, number]>;
}>): React.JSX.Element {
  return (
    <section className="push-aggregate-group" aria-label={title}>
      <h3>{title}</h3>
      <dl>
        {values.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
